import { useState, useRef, useCallback, useEffect } from 'react'

export type RecordingStatus = 'idle' | 'acquiring' | 'ready' | 'recording' | 'paused' | 'stopped' | 'error'

export interface UseVideoRecorderOptions {
  maxDurationSeconds?: number // Max recording duration (default: 180 = 3 minutes)
  video?: MediaTrackConstraints | boolean
  audio?: MediaTrackConstraints | boolean
  onDataAvailable?: (blob: Blob) => void
  onError?: (error: Error) => void
  onMaxDurationReached?: () => void
}

export interface UseVideoRecorderReturn {
  status: RecordingStatus
  error: Error | null
  previewStream: MediaStream | null
  recordedBlob: Blob | null
  recordedUrl: string | null
  duration: number // Current recording duration in seconds

  // Actions
  startCamera: () => Promise<void>
  stopCamera: () => void
  startRecording: () => void
  stopRecording: () => void
  pauseRecording: () => void
  resumeRecording: () => void
  resetRecording: () => void

  // Permissions
  hasPermission: boolean | null
  requestPermission: () => Promise<boolean>
}

const DEFAULT_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  facingMode: 'user',
}

const DEFAULT_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
}

export function useVideoRecorder(options: UseVideoRecorderOptions = {}): UseVideoRecorderReturn {
  const {
    maxDurationSeconds = 180, // 3 minutes default
    video = DEFAULT_VIDEO_CONSTRAINTS,
    audio = DEFAULT_AUDIO_CONSTRAINTS,
    onDataAvailable,
    onError,
    onMaxDurationReached,
  } = options

  const [status, setStatus] = useState<RecordingStatus>('idle')
  const [error, setError] = useState<Error | null>(null)
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null)
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null)
  const [duration, setDuration] = useState(0)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const durationIntervalRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewStream) {
        previewStream.getTracks().forEach((track) => track.stop())
      }
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl)
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
      }
    }
  }, [])

  // Check for camera/mic permissions
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })
      // Stop tracks immediately - we just wanted to check permission
      stream.getTracks().forEach((track) => track.stop())
      setHasPermission(true)
      return true
    } catch (err) {
      setHasPermission(false)
      return false
    }
  }, [])

  // Start camera preview
  const startCamera = useCallback(async () => {
    setStatus('acquiring')
    setError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video,
        audio,
      })

      setPreviewStream(stream)
      setHasPermission(true)
      setStatus('ready')
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to access camera')
      setError(error)
      setStatus('error')
      setHasPermission(false)
      onError?.(error)
    }
  }, [video, audio, onError])

  // Stop camera preview
  const stopCamera = useCallback(() => {
    if (previewStream) {
      previewStream.getTracks().forEach((track) => track.stop())
      setPreviewStream(null)
    }
    setStatus('idle')
  }, [previewStream])

  // Start recording
  const startRecording = useCallback(() => {
    if (!previewStream) {
      const error = new Error('No camera stream available')
      setError(error)
      onError?.(error)
      return
    }

    chunksRef.current = []
    setRecordedBlob(null)
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl)
      setRecordedUrl(null)
    }

    // Determine best supported mime type
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
      ? 'video/webm;codecs=vp8,opus'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : 'video/mp4'

    try {
      const recorder = new MediaRecorder(previewStream, {
        mimeType,
        videoBitsPerSecond: 2500000, // 2.5 Mbps
      })

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setRecordedBlob(blob)
        const url = URL.createObjectURL(blob)
        setRecordedUrl(url)
        onDataAvailable?.(blob)

        // Clear duration interval
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current)
          durationIntervalRef.current = null
        }
      }

      recorder.onerror = () => {
        const error = new Error('Recording error occurred')
        setError(error)
        setStatus('error')
        onError?.(error)
      }

      mediaRecorderRef.current = recorder
      recorder.start(1000) // Collect data every second

      // Start duration tracking
      startTimeRef.current = Date.now()
      setDuration(0)
      durationIntervalRef.current = window.setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
          setDuration(elapsed)

          // Check max duration
          if (elapsed >= maxDurationSeconds) {
            recorder.stop()
            setStatus('stopped')
            onMaxDurationReached?.()
          }
        }
      }, 1000)

      setStatus('recording')
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to start recording')
      setError(error)
      setStatus('error')
      onError?.(error)
    }
  }, [previewStream, recordedUrl, maxDurationSeconds, onDataAvailable, onError, onMaxDurationReached])

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
      setStatus('stopped')
    }
  }, [])

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause()
      setStatus('paused')

      // Pause duration tracking
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current)
        durationIntervalRef.current = null
      }
    }
  }, [])

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume()
      setStatus('recording')

      // Resume duration tracking
      const pausedDuration = duration
      startTimeRef.current = Date.now() - pausedDuration * 1000
      durationIntervalRef.current = window.setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000)
          setDuration(elapsed)

          if (elapsed >= maxDurationSeconds) {
            mediaRecorderRef.current?.stop()
            setStatus('stopped')
            onMaxDurationReached?.()
          }
        }
      }, 1000)
    }
  }, [duration, maxDurationSeconds, onMaxDurationReached])

  // Reset recording (keep camera on, clear recorded video)
  const resetRecording = useCallback(() => {
    chunksRef.current = []
    setRecordedBlob(null)
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl)
      setRecordedUrl(null)
    }
    setDuration(0)
    setError(null)

    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }

    if (previewStream) {
      setStatus('ready')
    } else {
      setStatus('idle')
    }
  }, [recordedUrl, previewStream])

  return {
    status,
    error,
    previewStream,
    recordedBlob,
    recordedUrl,
    duration,
    startCamera,
    stopCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecording,
    hasPermission,
    requestPermission,
  }
}
